/**
 * B.7.c.2 — Token-health signal for MicrosoftAccountPanel.
 *
 * One signal: `tokenStatus: 'valid' | 'expiring-soon' | 'expired' | 'unknown'`.
 *   - `valid`         — silent-acquire succeeded + >10 min until expiresOn.
 *   - `expiring-soon` — silent-acquire succeeded but <10 min until expiresOn.
 *   - `expired`       — silent-acquire threw `InteractionRequiredAuthError`
 *                       OR a recent withRetry call observed a 401 from Graph.
 *   - `unknown`       — initial state before MSAL has been probed.
 *
 * UI subscribes via `subscribeTokenStatus(listener)` + a one-shot
 * `getTokenStatus()` to read the current value. Lazy refresh: nobody
 * calls a cron; backend `withRetry` on a 401 dispatches `expired`.
 */

export type TokenStatus = 'valid' | 'expiring-soon' | 'expired' | 'unknown';

interface TokenStatusState {
  status: TokenStatus;
  /** Optional expiresOn ISO timestamp when 'valid' / 'expiring-soon'. */
  expiresOn?: string;
  /** Email address for the connected account, when known. */
  accountEmail?: string;
}

const EXPIRING_SOON_THRESHOLD_MS = 10 * 60 * 1000;

let current: TokenStatusState = { status: 'unknown' };
type Listener = (next: TokenStatusState) => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) {
    try {
      l(current);
    } catch {
      // listener errors must not break the dispatch loop
    }
  }
}

export function getTokenStatus(): TokenStatusState {
  return current;
}

export function subscribeTokenStatus(listener: Listener): () => void {
  listeners.add(listener);
  // Push current state to new subscribers so they have a value immediately.
  try {
    listener(current);
  } catch {
    // listener errors during initial push must not throw out of subscribe
  }
  return () => listeners.delete(listener);
}

/**
 * Pure classifier — given an `expiresOn` timestamp (Date or ISO string)
 * and `now`, return the status that should be reflected.
 */
export function classifyTokenStatus(
  expiresOn: Date | string | null,
  now: Date = new Date(),
): TokenStatus {
  if (expiresOn === null) return 'unknown';
  const expiry =
    expiresOn instanceof Date ? expiresOn : new Date(expiresOn);
  if (Number.isNaN(expiry.getTime())) return 'unknown';
  const diff = expiry.getTime() - now.getTime();
  if (diff <= 0) return 'expired';
  if (diff < EXPIRING_SOON_THRESHOLD_MS) return 'expiring-soon';
  return 'valid';
}

export function setTokenStatusFromExpiry(
  expiresOn: Date | string | null,
  accountEmail?: string,
): void {
  const status = classifyTokenStatus(expiresOn);
  current = {
    status,
    ...(expiresOn !== null
      ? {
          expiresOn:
            expiresOn instanceof Date
              ? expiresOn.toISOString()
              : expiresOn,
        }
      : {}),
    ...(accountEmail !== undefined ? { accountEmail } : {}),
  };
  notify();
}

export function markTokenExpired(): void {
  current = {
    status: 'expired',
    ...(current.accountEmail !== undefined
      ? { accountEmail: current.accountEmail }
      : {}),
  };
  notify();
}

export function markTokenValid(args: {
  expiresOn: Date | string;
  accountEmail?: string;
}): void {
  const status = classifyTokenStatus(args.expiresOn);
  current = {
    status,
    expiresOn:
      args.expiresOn instanceof Date
        ? args.expiresOn.toISOString()
        : args.expiresOn,
    ...(args.accountEmail !== undefined ? { accountEmail: args.accountEmail } : {}),
  };
  notify();
}

/**
 * Test-only reset. Clears the singleton state + all subscribers. Not
 * exported through index.ts.
 */
export function __resetTokenStatusForTests(): void {
  current = { status: 'unknown' };
  listeners.clear();
}
